import { Link } from 'react-router-dom';
import * as SingleSelect from '@givebutter/primitives/single-select';
import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { transformGeneratePdfLetterForToReadable } from 'assets/js/pages/engage/letters/transform';
import { LetterDraftFormValues } from 'assets/js/pages/engage/letters/types';
import { useCurrentLetter } from 'assets/js/pages/engage/letters/useCurrentLetter';
import { useLetterMeta } from 'assets/js/pages/engage/letters/useLetterMeta';
import * as Form from 'components/form';
import { AddressReadable } from 'components/pages/campaign-settings/campaign-auction/AddressReadable';
import { AddressType } from 'constants/addresses';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useInfiniteAccountAddresses } from 'customHooks/useInfiniteAccountAddresses';
import { inlineAddress } from 'helpers/address';
import { useFlags } from 'launchdarkly-react-client-sdk';

const LetterDetailsForm = () => {
    const { accountId } = useCurrentAccount();
    const { data: letter } = useCurrentLetter();
    const { isEasySend } = useLetterMeta();
    const { ref, data } = useInfiniteAccountAddresses({ type: AddressType['Mailing'] });
    const addresses = data?.pages ?? [];

    const { control, path } = Form.useGivebutterFormContext<LetterDraftFormValues>();
    const { engageLettersCompanyType, mailingsDoubleSided } = useFlags();

    return (
        <div className={'mx-auto flex w-full max-w-md flex-col gap-6 px-4 md:px-0'}>
            <Form.Container>
                <Form.Label name={path('name')}>
                    Letter name
                    <Form.LabelAsterisk />
                </Form.Label>
                <Form.TextInputRoot control={control} name={'name'}>
                    <Form.TextInputForm
                        placeholder={'Fall 2023 Solicitation Letter'}
                        onKeyDown={e => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                            }
                        }}
                    />
                </Form.TextInputRoot>
                <p className={'text-sm text-gray-700'}>For internal identification purposes only</p>
                <Form.Error name={path('name')} />
            </Form.Container>
            <Form.Container>
                <Form.Label name={path('returnAddress')}>
                    Return address
                    <Form.LabelAsterisk />
                </Form.Label>
                <Form.SingleSelect control={control} name="returnAddress">
                    <SingleSelect.Trigger aria-label="Return address">
                        <SingleSelect.Value placeholder="Select address" />
                    </SingleSelect.Trigger>
                    <SingleSelect.Content>
                        {addresses.map(address => (
                            <SingleSelect.Item value={address.id.toString()} key={address.id}>
                                <div className="flex flex-col gap-0.5">
                                    {address.name}
                                    <AddressReadable address={address} />
                                </div>
                                <div className="sr-only" aria-hidden>
                                    <SingleSelect.ItemText>
                                        {address.name} {inlineAddress(address)}
                                    </SingleSelect.ItemText>
                                </div>
                            </SingleSelect.Item>
                        ))}
                        <div ref={ref} />

                        <Link
                            to={`/accounts/${accountId}/engage/letters/draft/${letter.id}/edit/details/addresses/new/mailing`}
                            className="block px-2 py-3 text-sm font-medium"
                        >
                            <FontAwesomeIcon icon={faPlus} /> Add new return address
                        </Link>
                    </SingleSelect.Content>
                </Form.SingleSelect>
                <Form.UseFormError name={path('returnAddress')} />
            </Form.Container>
            <Form.Container>
                <Form.Label name={path('generateFor')}>Generate a letter for</Form.Label>

                <Form.SingleSelect control={control} name="generateFor">
                    <SingleSelect.Trigger aria-label="Generate a letter for" />
                    <SingleSelect.Content>
                        <SingleSelect.Item
                            value="all"
                            className="w-[--radix-select-trigger-width] flex-col items-start"
                        >
                            <p className={'font-medium'}>
                                <SingleSelect.ItemText>
                                    {transformGeneratePdfLetterForToReadable('all')}
                                </SingleSelect.ItemText>{' '}
                                <span className={'text-gray-500'}>(Default)</span>
                            </p>
                            <p className={'font-normal text-gray-600'}>
                                One letter per household plus individual contacts not associated
                                with a household
                            </p>
                        </SingleSelect.Item>
                        <SingleSelect.Item
                            value="individuals"
                            className="w-[--radix-select-trigger-width] flex-col items-start"
                        >
                            <p className="font-medium">
                                <SingleSelect.ItemText>
                                    {transformGeneratePdfLetterForToReadable('individuals')}
                                </SingleSelect.ItemText>
                            </p>
                            <p className="font-normal text-gray-600">
                                One letter per contact, regardless of household association
                            </p>
                        </SingleSelect.Item>
                        <SingleSelect.Item
                            value="households_only"
                            className="w-[--radix-select-trigger-width] flex-col items-start"
                        >
                            <p className="font-medium">
                                <SingleSelect.ItemText>
                                    {transformGeneratePdfLetterForToReadable('households_only')}
                                </SingleSelect.ItemText>
                            </p>
                            <p className="font-normal text-gray-600">
                                One letter per household. The head of household will be the primary
                                contact for the letter.
                            </p>
                        </SingleSelect.Item>
                        {engageLettersCompanyType && (
                            <SingleSelect.Item
                                value="companies"
                                className="w-[--radix-select-trigger-width] flex-col items-start"
                            >
                                <p className="font-medium">
                                    <SingleSelect.ItemText>
                                        {transformGeneratePdfLetterForToReadable('companies')}
                                    </SingleSelect.ItemText>
                                </p>
                                <p className="font-normal text-gray-600">
                                    One letter per company contact
                                </p>
                            </SingleSelect.Item>
                        )}
                    </SingleSelect.Content>
                </Form.SingleSelect>
            </Form.Container>
            {!isEasySend && (
                <div className={'flex items-start gap-2'}>
                    <div className="flex h-auto">
                        <Form.UseFormCheckboxInput
                            control={control}
                            name={'includeContactsWithNoAddress'}
                        />
                    </div>
                    <Form.Container>
                        <Form.Label
                            className={'mb-0.5 leading-none'}
                            name={path('includeContactsWithNoAddress')}
                        >
                            Include contacts with no address
                        </Form.Label>
                        <p className={'text-sm text-gray-700'}>
                            When checked, letters will be generated for all contacts, whether or not
                            an address is present. Leave unchecked to only generate letters for
                            contacts with an address.
                        </p>
                    </Form.Container>
                </div>
            )}
            {isEasySend && (
                <div className={'flex flex-col gap-4'}>
                    <div className={'flex items-start gap-2'}>
                        <div className="flex h-auto">
                            <Form.UseFormCheckboxInput
                                control={control}
                                name={path('excludeContactsMissingMergeFields')}
                            />
                        </div>
                        <Form.Container>
                            <Form.Label
                                className={'mb-0.5 leading-none'}
                                name={path('excludeContactsMissingMergeFields')}
                            >
                                Exclude contacts with missing merge fields
                            </Form.Label>
                            <p className={'text-sm text-gray-700'}>
                                When checked, contacts without merge field values will be excluded
                                from the recipient list.
                            </p>
                        </Form.Container>
                    </div>
                    {mailingsDoubleSided && (
                        <div className={'flex items-start gap-2'}>
                            <div className="flex h-auto">
                                <Form.UseFormCheckboxInput
                                    control={control}
                                    name={path('letterSettings.isDoubleSided')}
                                />
                            </div>
                            <Form.Container className={'gap-1'}>
                                <Form.Label
                                    className={'leading-none'}
                                    name={path('letterSettings.isDoubleSided')}
                                >
                                    Print double-sided
                                </Form.Label>
                                <p className={'text-sm text-gray-700'}>
                                    Check this box to print the letter on both sides of the paper.
                                </p>
                            </Form.Container>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export { LetterDetailsForm };
